import React from 'react';
import Spinner from 'components/Spinner';
import PropTypes from 'prop-types';
import Component from 'BaseComponent';
import api from 'api';
import autobind from 'autobind-decorator';
import { alert } from 'ducks/notifications';
import { query } from 'redux-bees';
import OperationButton from 'components/OperationButton';
import formatBytes from 'bytes';
import Time from 'components/Time';
import Tooltip from 'components/Tooltip';
import { withRouter } from 'react-router-dom';
import Pagination from 'components/Pagination';
import { connect } from 'react-redux';

const toRenderProp = ({ children, ...rest }) => children(rest);
const createQueryProvider = (...args) => query(...args)(toRenderProp);
const PaginationProvider = createQueryProvider('backups', api.getBackups, (perform, props) => (
  perform({ siteId: props.site.id, page: props.params.page })
));


@withRouter
@connect()

export default class Backups extends Component {
  static propTypes = {
    dispatch: PropTypes.func,
    site: PropTypes.object.isRequired,
  }

  state = { page: '1' }

  @autobind
  handlePageChange(page) {
    this.setState({ page });
  }

  @autobind
  handleDownload(id) {
    const { dispatch, site } = this.props;
    return dispatch(api.requestBackupDownload({ siteId: site.id, id }, null))
      .catch(({ body }) => dispatch(alert(this.t(`error.${body.errors[0].code}`))));
  }

  @autobind
  handleRestore(id) {
    const { dispatch, site } = this.props;

    if (confirm('Are you sure?')) {
      return dispatch(api.restoreBackup({ siteId: site.id, id }, null))
        .catch(({ body }) => dispatch(alert(this.t(`error.${body.errors[0].code}`))));
    }
    return true;
  }

  @autobind
  renderBackup(backup) {
    const { site } = this.props;
    const capitalize = (s) => {
      if (typeof s !== 'string') return '';
      return s.charAt(0).toUpperCase() + s.slice(1);
    };

    return (
      <div className="Backups__item" key={backup.id}>

        <div className="Backups__item__cell Backups__item__cell--deploy">
          { backup.relationships.deploy.data &&
          <Tooltip label="Automatically generated by a deploy">
            <div className="Backups__item__deploy">
              <span>{backup.id}</span>
            </div>
          </Tooltip>
          }
          { !backup.relationships.deploy.data &&
            <Tooltip label="Automatically generated by WordPress turned OFF">
              <div className="Backups__item__paused">
                <span>{backup.id}</span>
              </div>
            </Tooltip>
          }
        </div>
        <div className="Backups__item__cell Backups__item__cell--date">
          <div className="Backups__item__title">
            { backup.attributes.state === 'git' &&
            <span>Created </span>
            }
            { backup.attributes.state !== 'git' &&
              <span>{ capitalize(backup.attributes.state) } </span>
            }
            <Time value={backup.attributes.updatedAt} />
          </div>
        </div>
        <div className="Backups__item__cell Backups__item__cell--size">
          { (backup.attributes.state === 'completed' || backup.attributes.state === 'git') &&
              backup.attributes.size &&
              <div>{formatBytes(backup.attributes.size + 0, { unitSeparator: ' ' })}</div>
          }
        </div>
        <div className="Backups__item__cell Backups__item__cell--download">
          <div>
            { backup.relationships.download.data && backup.attributes.downloadGeneratedAt &&
            <a className="button button--tiny button--primary" href={backup.attributes.downloadUrl}>
              <i className="icon--download" />
            </a>
            }
            { backup.relationships.download.data && !backup.attributes.downloadGeneratedAt &&
              <Tooltip label="Archive is being generated...">
                <Spinner size={20} className="Spinner" />
              </Tooltip>
            }
            { !backup.relationships.download.data && (backup.attributes.state === 'completed' || (backup.attributes.state === 'git' && site.attributes.wpState === 'paused')) &&
              <Tooltip label="Generate a backup archive. Depending on the size of the site, it may take several minutes">
                <button
                  onClick={() => this.handleDownload(backup.id)}
                  className="button button--tiny"
                >
                  <i className="icon--download" />
                </button>
              </Tooltip>
            }
            { !backup.relationships.download.data && backup.attributes.state === 'git' && site.attributes.wpState !== 'paused' &&
              <Tooltip label="Switch WordPress OFF to download this backup">
                <button
                  className="button button--tiny"
                  disabled
                >
                  <i className="icon--download" />
                </button>
              </Tooltip>
            }
          </div>
        </div>

        { backup.attributes.showRestoreButton ?
          <div className="Backups__item__cell Backups__item__cell--actions">
            {site.attributes.wpState !== 'paused' ?
              <Tooltip label="Switch WordPress OFF to restore this backup">
                <OperationButton
                  className="button button--tiny"
                  disabled
                  label={[
                    <i className="icon--restore" />,
                    <span>Restore this backup</span>,
                  ]}
                />
              </Tooltip>
              :
              <OperationButton
                onClick={() => this.handleRestore(backup.id)}
                className="button button--tiny"
                label={[
                  <i className="icon--restore" />,
                  <span>Restore this backup</span>,
                ]}
              />
            }
          </div>
          :
          <div className="Backups__item__cell Backups__item__cell--actions" />
        }

      </div>
    );
  }

  renderLoading() {
    return (
      <div className="spinner-container">
        <Spinner />
      </div>
    );
  }

  render() {
    const { site } = this.props;
    return (
      <PaginationProvider params={this.state} site={site}>
        { ({ backups, status }) => (
          <div>
            <div className="Deploys__bar">
              <div className="Deploys__bar__left">
                {
                  site.attributes.wpState === 'running' ?
                    <div>
                      WordPress is currently running.
                      You have to switch WordPress OFF to restore a backup.
                    </div>
                    :
                    <div>
                      A backup is automatically created every time you create a new deploy
                      or turn your WordPress OFF.
                      Restoring a backup restore your WP files and database,
                      but do not change your live static website.
                    </div>
                }
              </div>
            </div>

            { backups && backups.length > 0 &&
            <div>
              <div className="Backups__list">
                <div className="Backups__item Backups__item__header">
                  <div className="Backups__item__cell Backups__item__cell--deploy">
                    ID
                  </div>
                  <div className="Backups__item__cell Backups__item__cell--date">
                    Creation date
                  </div>
                  <div className="Backups__item__cell Backups__item__cell--size">
                    Size
                  </div>
                  <div className="Backups__item__cell Backups--item_cell--download" >
                    Download
                  </div>
                  <div className="Backups__item__cell Backups__item__cell--actions" />
                </div>
                {backups.map(this.renderBackup)}
              </div>
              <Pagination
                data={status.backups.meta.pagination}
                handlePageChange={this.handlePageChange}
              />
            </div>
            }
          </div>
        )}
      </PaginationProvider>
    );
  }
}



// WEBPACK FOOTER //
// ./app/components/Backups.jsx